// **********************************************************************
//
// WebAudioTrack
//
// A WebAudio Track with an interface that matches a HTML Audio Element
//
// **********************************************************************

import EventEmitter from '@/helpers/EventEmitter'
import { context, masterGain } from 'src/helpers/WebAudio'

const Events = {
  LOAD: 'load',
  END: 'end',
  PLAY: 'play',
  PAUSE: 'pause',
  STATE: 'state',
}

class WebAudioTrack {
  constructor(trackConfig) {
    Object.assign(this, EventEmitter)

    // CONFIG
    this.id = trackConfig.id
    this.config = JSON.parse(JSON.stringify(trackConfig))

    this.state = ''
    this.Events = Events

    // LOAD STATE
    this.loading = {
      progress: 0,
      ready: false,
      completed: false,
    }

    // OBJECTS
    this.gainNode = context.createGain()
    this.gainNode.connect(masterGain)
    this.gainNode.gain.value = 0

    this.buffer = null
    this.source = null

    this.playing = false
    this.isEnded = false
    this.isMuted = false
    this.currVolume = 0
    this._muteVolume = 1
    this.time = 0
  }

  load = () => {
    return new Promise((resolve, reject) => {
      const onLoad = () => {
        context.decodeAudioData(xhr.response, (buffer) => {
          this.buffer = buffer
          this._setState(Events.LOAD)
          resolve()
        })
      }

      const onProgress = (evt) => {
        let progress = 0
        if (evt && evt.lengthComputable) {
          progress = evt.loaded / evt.total
        }
        this.loading.progress = progress
      }

      let xhr = new XMLHttpRequest()

      xhr.onprogress = onProgress
      xhr.onload = onLoad
      xhr.onerror = reject
      xhr.onabort = reject

      xhr.open('GET', this.config.src[0].url)
      xhr.responseType = 'arraybuffer'
      xhr.send()
    })
  }

  _setState = (type) => {
    this.state = type
    this.emit(type)
    this.emit(Events.STATE, type)
  }

  _onEnded = () => {
    this.playing = false
    this.isEnded = true
    this.currTime = this.buffer.duration
    this._setState(Events.END)
  }

  destroySource = () => {
    if (this.source) {
      this.source.onended = null
      try {
        this.source.stop()
        this.source.disconnect()
      } catch (err) {
        console.warn(err)
      }
      this.source = null
    }
  }

  play = () => {
    //if(this.playing){return}
    this.destroySource()

    this.source = context.createBufferSource()
    this.source.buffer = this.buffer

    // short volume ramp to prevent clicking noise
    this.gainNode.gain.setValueAtTime(0, context.currentTime)
    this.source.connect(this.gainNode)
    this.gainNode.gain.linearRampToValueAtTime(this.compoundVolume, context.currentTime + 0.1)

    this._playOffset = this.time
    this.source.start(0, this.time)
    this.startTime = window.performance.now()
    this.playing = true
    this.source.onended = this._onEnded
    this._setState(Events.PLAY)
  }

  _getPlayedOffset = () => {
    //note estimate only - affected by tab visible/ other slowdown factors
    return (window.performance.now() - this.startTime) / 1000 + this._playOffset
  }

  pause = () => {
    if (!this.playing) return
    if (!this.source) return
    this.destroySource()

    this.time = this._getPlayedOffset()
    this.playing = false
    this._setState(Events.PAUSE)
  }

  get compoundVolume() {
    return this.currVolume * this._muteVolume
  }

  updateCompoundVolume() {
    if (this.prevCompoundVolume !== this.compoundVolume) {
      this.gainNode.gain.value = this.compoundVolume
    }
    this.prevCompoundVolume = this.compoundVolume
  }

  get volume() {
    return this.currVolume
  }

  set volume(val) {
    // let changed = (this.currVolume !== val)
    this.currVolume = val
    this.updateCompoundVolume()
  }

  get muted() {
    return this.isMuted
  }

  set muted(val) {
    this.isMuted = val
    this._muteVolume = this.isMuted ? 0 : 1
    this.updateCompoundVolume()
  }

  get currentTime() {
    if (this.playing) {
      return this._getPlayedOffset()
    } else {
      return this.time
    }
  }

  set currentTime(val) {
    this.time = val

    this.destroySource()

    if (val > this.buffer.duration) {
      this._onEnded()
    } else {
      this.isEnded = false
      if (!this.paused) {
        this.play()
      }
    }
  }

  get duration() {
    return this.buffer.duration
  }

  get paused() {
    return !this.playing
  }

  get ended() {
    return this.isEnded
  }
}

export default WebAudioTrack
